<template>
  <div class="location-social-metrics-page">
    <div v-if="isLoading" style="margin-bottom: 35px; display: flex; flex-direction: column; align-items: center; text-align: center">
      <LoaderNew style="width: 70px; height: 70px; margin-bottom: 10px" :borderWidth="3" />
    </div>

    <div v-else-if="noCalendarsFound" class="no-calendars-message" style="margin-bottom: 35px; display: flex; flex-direction: column; align-items: center; text-align: center">
      <h2 class="global-h2">No Calendars Found</h2>
      <p class="global-p">Please contact Shrpa to get started.</p>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="location-social-metrics-page__header">
        <div class="location-social-metrics-page__header-copy">
          <CalendarPicker :customerId="customerId" suffix="Social Analytics" />
          <p class="global-p location-social-metrics-page__subtitle">See the reach and engagement generated by your published posts.</p>
        </div>

        <div class="location-social-metrics-page__filters">
          <LoadingIcon v-if="isRefreshing" :size="16" />
          <ReportDateRangePicker :modelValue="dateRange" @update:modelValue="value => (dateRange = value)" />
          <ReportFilterDropdown label="Network" :options="networkOptions" :modelValue="networkFilter" @update:modelValue="value => (networkFilter = value)" />
        </div>
      </div>

      <!-- Load error -->
      <NoteWithIcon v-if="loadError" class="location-social-metrics-page__note" color="red" size="small">
        <template #text>
          <div>{{ loadError }}</div>
          <LinkWithIcon class="location-social-metrics-page__retry" isDottedUnderline color="red" @click="loadReport(true)">Try again</LinkWithIcon>
        </template>
      </NoteWithIcon>

      <!-- Report body -->
      <template v-if="report">
        <!-- No taggable accounts saved yet -->
        <NoteWithIcon v-if="report.locationCount === 0" class="location-social-metrics-page__note" color="gray" size="small">
          <template #text>
            <div>
              No taggable accounts saved yet.
              <router-link :to="{ name: 'CalendarConfig', params: { customerId } }">Add locations in Configuration</router-link>
              to start tracking local promotion.
            </div>
          </template>
        </NoteWithIcon>

        <!-- First sync hasn't finished yet -->
        <NoteWithIcon v-if="report.isSyncPending" class="location-social-metrics-page__note" color="gray" size="small">
          <template #text>
            <div>We're gathering metrics for your posts. Check back in a few minutes.</div>
            <LinkWithIcon class="location-social-metrics-page__retry" isDottedUnderline color="gray" @click="loadReport(true)">
              <template #icon><IconEmbedded name="reload_2" :size="14" /></template>
              Refresh
            </LinkWithIcon>
          </template>
        </NoteWithIcon>

        <ReportKpiTiles class="location-social-metrics-page__tiles" :tiles="kpiTiles" />

        <div class="location-social-metrics-page__tabs">
          <SrpSegmentedControl :modelValue="activeTab" :options="tabOptions" size="sm" ariaLabel="Report section" @update:modelValue="value => setActiveTab(value as ActiveTab)" />
        </div>

        <NoteWithIcon v-if="report.posts.length === 0" class="location-social-metrics-page__note" color="gray" size="small">
          <template #text><div>No posts match these filters</div></template>
        </NoteWithIcon>

        <template v-else>
          <!-- Overview -->
          <template v-if="activeTab === 'overview'">
            <div class="location-social-metrics-page__cards-row">
              <PerformanceByLocationCard :rows="report.rows" :limit="5" />
              <EngagementMixCard :engagementMix="report.engagementMix" :channels="engagementMixChannels" />
            </div>

            <LocationSocialMetricsTable
              :rows="highlightedRows"
              title="Top locations"
              :limit="5"
              :isSearchVisible="false"
              @viewPosts="showPostsForLocation"
              @viewAllLocations="setActiveTab('locations')"
            />
          </template>

          <!-- Locations -->
          <LocationSocialMetricsTable v-else-if="activeTab === 'locations'" ref="locationsTableRef" :rows="report.rows" title="Results by location" @viewPosts="showPostsForLocation" />

          <!-- Posts -->
          <LocationSocialPostsTable v-else :posts="report.posts" :rows="report.rows" :locationId="postsLocationId" @clearLocationFilter="postsLocationId = null" />
        </template>

        <!-- Footer -->
        <div v-if="metricsUpdatedText" class="location-social-metrics-page__footer">{{ metricsUpdatedText }}</div>
      </template>
    </template>

    <div style="margin-top: 50px; display: flex; justify-content: flex-end; width: 100%">
      <SuperUserActionPanel>
        <div>
          <SrpButton color="gray" fill="outlined" size="tiny" :to="{ name: 'LocationHighlightsReport', params: { customerId } }">Locations Report</SrpButton>
        </div>
        <div v-if="(report?.postsMissingAnalyticsCount ?? 0) > 0" class="location-social-metrics-page__admin-missing">
          {{ report?.postsMissingAnalyticsCount }} {{ report?.postsMissingAnalyticsCount === 1 ? "post doesn't" : "posts don't" }} have metrics yet
        </div>
      </SuperUserActionPanel>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useHead } from "@unhead/vue";
import { MetricSender } from "@/helpers/MetricSender";
import { getGlobalRemoteLogger } from "@/helpers/RemoteLogger";
import AdminContext from "@logic/AdminContext";
import DayjsExtended from "@logic/DayjsExtended";
import NumberFormatting from "@logic/NumberFormatting";
import { getShowTestCalendars } from "@logic/calendarManager";
import { getSocialChannelLabel } from "@contracts/socialNetworks";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import SocialReportsRepo from "@repos/SocialReportsRepo";

// Components
import CalendarPicker from "@components/CalendarPicker.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import LoaderNew from "@components/Loader/LoaderNew.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpSegmentedControl from "@components/ui/SrpSegmentedControl.vue";
import EngagementMixCard from "./LocationSocialMetrics/EngagementMixCard.vue";
import LocationSocialMetricsTable from "./LocationSocialMetrics/LocationSocialMetricsTable.vue";
import LocationSocialPostsTable from "./LocationSocialMetrics/LocationSocialPostsTable.vue";
import PerformanceByLocationCard from "./LocationSocialMetrics/PerformanceByLocationCard.vue";
import ReportDateRangePicker from "./shared/ReportDateRangePicker.vue";
import ReportFilterDropdown from "./shared/ReportFilterDropdown.vue";
import ReportKpiTiles from "./shared/ReportKpiTiles.vue";

// Types
import type { ReportFilterOption } from "./shared/ReportFilterDropdown.vue";
import type { ReportKpiTile } from "./shared/ReportKpiTiles.vue";
import { resolveReportDateRange, type ReportDateRangeValue } from "./shared/reportDateRange";
import type { LocationSocialMetricsReport, LocationSocialMetricsRow, SocialReportChannel } from "@contracts/socialReports";

defineOptions({
  name: "LocationSocialMetricsReport",
});

type ActiveTab = "overview" | "locations" | "posts";

/** Sentinel for the Network dropdown: no `channel` query param is sent. */
const ALL_NETWORKS = "all";

// Router
const route = useRoute();

// Normalize route params ------------------------------------------------------
const customerId = computed<string>(() => (Array.isArray(route.params.customerId) ? route.params.customerId[0] : (route.params.customerId as string)));

const pageTitle = ref("Social Analytics");
useHead({ title: () => pageTitle.value });

// State ======================================================================
const report = ref<LocationSocialMetricsReport | null>(null);
const dateRange = ref<ReportDateRangeValue>(resolveReportDateRange("allTime"));
const networkFilter = ref<string>(ALL_NETWORKS);
const activeTab = ref<ActiveTab>("overview");
const postsLocationId = ref<string | null>(null);
const isLoading = ref<boolean>(true);
const isRefreshing = ref<boolean>(false);
const loadError = ref<string>("");

const noCalendarsFound = ref<boolean>(false);
const hasAppliedDefaultTab = ref<boolean>(false);

const locationsTableRef = ref<InstanceType<typeof LocationSocialMetricsTable> | null>(null);

// Filters ====================================================================
const networkOptions = computed<ReportFilterOption[]>(() => [
  { value: ALL_NETWORKS, label: "All" },
  ...(report.value?.availableChannels ?? []).map(channel => ({ value: channel, label: getSocialChannelLabel(channel) })),
]);

/** Engagement mix logos follow the Network filter; the dropdown still lists every reached network. */
const engagementMixChannels = computed<SocialReportChannel[]>(() => {
  if (networkFilter.value !== ALL_NETWORKS) return [networkFilter.value as SocialReportChannel];
  return report.value?.availableChannels ?? [];
});

const tabOptions = computed(() => [
  { value: "overview", label: "Overview" },
  { value: "locations", label: `Locations (${report.value?.rows?.length ?? 0})` },
  { value: "posts", label: `Posts (${report.value?.posts?.length ?? 0})` },
]);

// Derived ====================================================================
const highlightedRows = computed<LocationSocialMetricsRow[]>(() => (report.value?.rows ?? []).filter(r => r.featuredPostCount > 0));

const kpiTiles = computed<ReportKpiTile[]>(() => {
  if (!report.value) return [];
  const tiles: ReportKpiTile[] = [
    { label: "Reach", value: NumberFormatting.abbreviatedFormat(report.value.reach) || "0" },
    { label: "Engagement", value: NumberFormatting.abbreviatedFormat(report.value.engagements) || "0" },
  ];
  const engagementRate = NumberFormatting.formatPercent(report.value.engagementRatePercent);
  if (engagementRate) tiles.push({ label: "Engagement rate", value: engagementRate });
  tiles.push(
    { label: "Posts featuring locations", value: `${report.value.featuredPostCount}` },
    { label: "Locations featured", value: `${report.value.locationsHighlightedCount}` }
  );
  return tiles;
});

const metricsUpdatedText = computed<string>(() => {
  const lastSyncedAt = report.value?.lastSyncedAt;
  if (!lastSyncedAt) return "";
  const date = DayjsExtended(lastSyncedAt);
  if (!date.isValid()) return "";
  return `Updated ${date.format("MMMM Do")}`;
});

// Load =======================================================================
async function loadReport(isRefresh: boolean) {
  if (isRefresh) isRefreshing.value = true;
  loadError.value = "";

  const channel = networkFilter.value === ALL_NETWORKS ? undefined : (networkFilter.value as SocialReportChannel);

  try {
    report.value = await SocialReportsRepo.getLocationSocialMetrics(customerId.value, channel, dateRange.value.startDate, dateRange.value.endDate);
    if (!hasAppliedDefaultTab.value) {
      hasAppliedDefaultTab.value = true;
      const hasLocationMetrics = report.value.rows.some(row => row.reach > 0 || row.engagements > 0);
      if (!hasLocationMetrics) activeTab.value = "posts";
    }
  } catch (error) {
    getGlobalRemoteLogger()?.error(`LocationSocialMetricsPage.loadReport failed. customerId=${customerId.value} channel=${channel ?? "all"} error=${error}`);
    loadError.value = "We couldn't load this report.";
  } finally {
    isRefreshing.value = false;
    isLoading.value = false;
  }
}

watch([networkFilter, dateRange], () => loadReport(true));

// Tabs =======================================================================
/** Picking a tab by hand always drops the "View posts" location filter. */
function setActiveTab(tab: ActiveTab) {
  postsLocationId.value = null;
  activeTab.value = tab;
}

// "View posts" drill-down ====================================================
async function showPostsForLocation(locationId: string) {
  postsLocationId.value = locationId;
  activeTab.value = "posts";
  locationsTableRef.value?.clearSearch();
  await nextTick();
  window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
}

onMounted(async () => {
  const org = await AdminContext.setOrgInContextFromCustomerId(customerId.value);
  if (org?.name) pageTitle.value = `Social Analytics - ${org.name}`;

  const calendars = await ContentCalendarRepo.getCalendarsForCustomer(customerId.value, getShowTestCalendars());
  noCalendarsFound.value = calendars.length === 0;

  if (noCalendarsFound.value) {
    isLoading.value = false;
    return;
  }

  MetricSender.sendMetric("Cust-Report-LocationSocialMetrics-PageView", { customerId: customerId.value });

  await loadReport(false);
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Location social metrics page ===============================================
.location-social-metrics-page {
  width: $desktop-wide-right-col-width;
  margin: 0 auto;

  &__header {
    margin-bottom: 22px;
    gap: 20px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  &__header-copy {
    min-width: 0;
    flex-grow: 1;
  }

  &__subtitle {
    margin: 7px 0 0;
  }

  &__filters {
    gap: 18px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding-bottom: 4px;
  }

  &__note {
    margin-bottom: 22px;
  }

  &__retry {
    margin-top: 6px;
  }

  &__tiles {
    margin-bottom: 20px;
  }

  &__tabs {
    margin-bottom: 20px;
  }

  &__cards-row {
    margin-bottom: 20px;
    gap: 20px;
    display: grid;
    grid-template-columns: 1.6fr 1fr;
    align-items: stretch;
  }

  &__footer {
    margin-top: 16px;
    color: #667085;
    font: 400 13px/18px sans-serif;
  }

  &__admin-missing {
    margin-top: 8px;
    color: #667085;
    font: 400 13px/18px sans-serif;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .location-social-metrics-page {
    width: $desktop-right-col-width;
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .location-social-metrics-page {
    width: $laptop-right-col-width;
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .location-social-metrics-page {
    width: $tablet-large-content-width;
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .location-social-metrics-page {
    width: $tablet-content-width;

    &__cards-row {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .location-social-metrics-page {
    width: $mobile-content-width;

    &__cards-row {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}
</style>
